import React, { useState, useRef } from "react";
import {
  Search,
  Sparkles,
  Copy,
  Check,
  History,
  ChevronDown,
  ChevronUp,
  ArrowRight,
  Lightbulb,
  ArrowUp,
} from "lucide-react";
import { toast } from "react-hot-toast";
import documentSearchService from "../../services/document-search.service";
import MarkdownRenderer from "./MarkdownRenderer";
import QueryHistoryTab, { QueryHistoryTabRef } from "./QueryHistoryTab";

interface DocumentSearchTabProps {
  documentStoreId: string;
}

const suggestedQueries = [
  "Summarize the key points of the document",
  "What are the important dates mentioned?",
  "List all names and entities referenced",
  "What are the main conclusions?",
];

const DocumentSearchTab: React.FC<DocumentSearchTabProps> = ({
  documentStoreId,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResult, setSearchResult] = useState<string | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const queryHistoryRef = useRef<QueryHistoryTabRef | null>(null);

  const handleSearch = async (query?: string) => {
    const q = query || searchQuery;
    if (!q.trim()) {
      toast.error("Please enter a search query");
      return;
    }
    if (query) setSearchQuery(query);
    setIsSearching(true);
    setSearchResult(null);
    try {
      const response = await documentSearchService.documentSearch(
        documentStoreId,
        q,
      );
      if (response.success) {
        setSearchResult(response.data);
        if (queryHistoryRef.current) {
          queryHistoryRef.current.refreshHistory();
        }
      } else {
        toast.error("Search failed. Please try again.");
      }
    } catch (error: any) {
      console.error("Error during search:", error);
      toast.error(error.response?.data?.message || "Failed to perform search");
    } finally {
      setIsSearching(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSearch();
    }
  };

  const handleCopy = () => {
    if (searchResult) {
      navigator.clipboard.writeText(searchResult);
      setCopied(true);
      toast.success("Copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="space-y-5">
      {/* Main Search + Response Card */}
      <div className="bg-white rounded-md border border-slate-200 overflow-hidden">
        {/* Input Section */}
        <div className="p-5 pb-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 rounded-md bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center">
              <Search className="w-3.5 h-3.5 text-white" />
            </div>
            <h2 className="text-sm font-bold text-slate-900">
              Ask a question about your documents
            </h2>
          </div>
          <div className="relative bg-slate-50 border border-slate-200 rounded-md focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-100 focus-within:bg-white transition-all">
            {/* Centered placeholder overlay */}
            {!searchQuery && (
              <div className="absolute bottom-3 left-4 pointer-events-none">
                <span className="text-sm text-slate-400">
                  What would you like to know?
                </span>
              </div>
            )}
            <textarea
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleKeyPress}
              disabled={isSearching}
              rows={4}
              className="w-full bg-transparent text-sm text-slate-800 resize-none outline-none disabled:opacity-60 leading-relaxed px-4 py-3"
              style={{ minHeight: "100px", maxHeight: "160px" }}
            />
            <div className="absolute bottom-2.5 right-2.5">
              <button
                onClick={() => handleSearch()}
                disabled={isSearching || !searchQuery.trim()}
                className="p-2 rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-all"
                title="Search"
              >
                {isSearching ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <ArrowUp className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>
          <p className="text-[10px] text-slate-400 mt-1.5">
            Press Enter to search · Shift+Enter for new line
          </p>
        </div>

        {/* Loading — inside same card */}
        {isSearching && (
          <div className="px-5 pb-5">
            <div className="border-t border-slate-100 pt-4">
              <div className="space-y-2.5">
                <div className="h-3 rounded-md w-[90%] ai-shimmer"></div>
                <div
                  className="h-3 rounded-md w-[70%] ai-shimmer"
                  style={{ animationDelay: "0.2s" }}
                ></div>
                <div
                  className="h-3 rounded-md w-[50%] ai-shimmer"
                  style={{ animationDelay: "0.4s" }}
                ></div>
              </div>
              <div className="relative h-4 overflow-hidden mt-2">
                <span className="text-xs text-indigo-500 font-medium ai-text-fade">
                  Analyzing documents...
                </span>
                <span
                  className="text-xs text-indigo-500 font-medium ai-text-fade"
                  style={{ animationDelay: "3s" }}
                >
                  Generating response...
                </span>
                <span
                  className="text-xs text-indigo-500 font-medium ai-text-fade"
                  style={{ animationDelay: "6s" }}
                >
                  Almost there...
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Response — inside same card */}
        {searchResult && !isSearching && (
          <div className="border-t border-slate-100">
            <div className="px-5 py-3 flex items-center justify-between bg-slate-50/50">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                  <Sparkles className="w-3 h-3 text-white" />
                </div>
                <span className="text-xs font-semibold text-slate-700">
                  AI Response
                </span>
              </div>
              <button
                onClick={handleCopy}
                className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-xs font-medium transition-all ${
                  copied
                    ? "bg-green-50 text-green-700 border-green-200"
                    : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50 hover:border-slate-300"
                }`}
              >
                {copied ? (
                  <>
                    <Check className="w-3 h-3" />
                    <span>Copied!</span>
                  </>
                ) : (
                  <>
                    <Copy className="w-3 h-3" />
                    <span>Copy</span>
                  </>
                )}
              </button>
            </div>
            <div className="px-5 py-4">
              <MarkdownRenderer content={searchResult} />
            </div>
          </div>
        )}

        {/* Empty State — inside same card */}
        {!searchResult && !isSearching && (
          <div className="px-5 pb-5">
            <div className="border-t border-slate-100 pt-4">
              <p className="text-xs text-slate-500 mb-3 font-medium">
                Try asking
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {suggestedQueries.map((query) => (
                  <button
                    key={query}
                    onClick={() => handleSearch(query)}
                    className="group flex items-center gap-2 text-left px-3 py-2 text-xs text-slate-600 bg-slate-50 hover:bg-blue-50 hover:text-blue-700 border border-slate-200 hover:border-blue-200 rounded-md transition-all"
                  >
                    <ArrowRight className="w-3 h-3 text-slate-400 group-hover:text-blue-500 flex-shrink-0 transition-colors" />
                    <span>{query}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Query History - Collapsible */}
      <div className="bg-white rounded-md border border-slate-200 overflow-hidden">
        <button
          onClick={() => setShowHistory(!showHistory)}
          className="w-full flex items-center justify-between px-5 py-3 hover:bg-slate-50 transition-colors"
        >
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
              <History className="w-3.5 h-3.5 text-white" />
            </div>
            <div className="text-left">
              <span className="text-sm font-bold text-slate-900 block">
                Query History
              </span>
              <span className="text-[10px] text-slate-400">
                View and regenerate your past searches
              </span>
            </div>
          </div>
          {showHistory ? (
            <ChevronUp className="w-4 h-4 text-slate-400" />
          ) : (
            <ChevronDown className="w-4 h-4 text-slate-400" />
          )}
        </button>
        {showHistory && (
          <div className="border-t border-slate-100 px-5 py-4">
            <QueryHistoryTab
              documentStoreId={documentStoreId}
              ref={queryHistoryRef}
            />
          </div>
        )}
      </div>

      {/* Tips Section */}
      <div className="bg-amber-50/50 border border-amber-100 rounded-md p-4">
        <div className="flex items-start gap-2.5">
          <Lightbulb className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-xs font-semibold text-amber-900 mb-1">
              Tips for better results
            </h4>
            <ul className="text-[11px] text-amber-800/70 space-y-1 leading-relaxed">
              <li>Be specific with your questions for more accurate answers</li>
              <li>
                Use <strong>AI Chat</strong> tab for follow-up questions that
                need conversation context
              </li>
              <li>
                Use <strong>Knowledge Base</strong> tab to compare answers
                across multiple files at once
              </li>
              <li>
                Check <strong>Query History</strong> to revisit or regenerate
                previous search results
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentSearchTab;
